/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Link } from 'carbon-components-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { sharedMessages } from '../components/messages';

interface LearnMoreLinkProps {
  url: string;
  children?: JSX.Element;
  onClick?: () => {};
  [key: string]: any;
}
const LearnMoreLink = (props: LearnMoreLinkProps) => {
  const { url, onClick, children, ...rest } = props;
  const linkText = children ? (
    children
  ) : (
    <FormattedMessage {...sharedMessages.learnMore} />
  );
  if (onClick && !url) {
    return (
      <Link
        className="link--learn-more"
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
        {...rest}>
        {linkText}
      </Link>
    );
  }
  return url ? (
    <Link
      href={url}
      className="link--learn-more"
      target="_blank"
      rel="noopener noreferrer"
      {...rest}>
      {linkText}
    </Link>
  ) : (
    <>{linkText}</>
  );
};

LearnMoreLink.propTypes = {
  url: PropTypes.string,
  linkText: PropTypes.string,
  children: PropTypes.node
};

/**
 * Used for react-intl messages to substitute to define
 * a function to replace the a set of text in a message.
 * Example:
 *  <FormattedMessage
 *   id="message1"
 *   defaultMessage="my link is <link>this</link>"
 *   values={{
 *     link: text => linkify(someurl, text)
 *   }}
 *
 * @param {string} link link to use for component
 * @param {string} text text of link
 * @param {func} onClick passed as prop to Link
 * @param {bool} disabled passed as prop to Link
 * @returns {Component} accepts text to wrap in link.
 */
const linkify = props => {
  // Text can be an array...
  const { linkText, ...rest } = props;
  return (
    <LearnMoreLink {...rest}>
      {Array.isArray(linkText) ? linkText[0] : linkText}
    </LearnMoreLink>
  );
};

/**
 * Used for react-intl messages with a <link> tag in them
 * Inserts LearnMoreLink with properties
 *
 * Example:
 * // with a message defines somewhere...
 * const message = defineMessages({
 *  message1: {
 *   id="message1"
 *   defaultMessage="my link is <link>this</link>"
 *  }
 * });
 *
 * const link = 'https://someurl.com';
 * <FormattedMessageWithLink
 *   url={link}
 *   {...message.message1}
 * />
 *
 * @param {string} url to use for component
 * @param {string} id MessageId from intl message
 * @param {string} defaultMessage defaultMessage from intl message
 * @param {func} onClick (optional) passed as prop to Link
 * @param {bool} disabled (optional) passed as prop to Link
 * @returns {Component} contructed component with links and intl text
 */
const FormattedMessageWithLink = props => {
  const { url, onClick, disabled, ...message } = props;
  return (
    <FormattedMessage
      {...message}
      values={{
        link: text => linkify({ url, linkText: text, onClick, disabled }),
        // eslint-disable-next-line react/display-name
        strong: text => <strong>{text}</strong>
      }}
    />
  );
};

FormattedMessageWithLink.propTypes = {
  url: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool
};

export { LearnMoreLink, FormattedMessageWithLink, linkify };
